// ============================================================================
//  DemoShell.jsx — the public /demo experience.
//
//  Wraps the real MileMarker UI in a fake auth context so no login is needed,
//  and adds a banner with a persona switcher: view the app as a mentee, a
//  mentor, or the admin. All data is fictional (see demoData.js) and lives
//  in memory only — refresh resets the world.
// ============================================================================

import { useState } from "react";
import { StaticAuthProvider } from "../auth/AuthContext.jsx";
import MileMarker from "../MileMarker.jsx";
import { DEMO_PERSONAS, buildDemoSeed } from "./demoData.js";
import { getPersonaId, setPersonaId } from "./demoMode.js";
import { getNoticeAck } from "./demoStorage.js";
import { C } from "../theme/tokens.js";

export default function DemoShell({ initialView }) {
  const [personaId, setPersona] = useState(getPersonaId);

  const seed = buildDemoSeed();
  const person = seed.people[personaId];
  const persona = DEMO_PERSONAS.find((p) => p.id === personaId);

  const session = { user: { id: personaId, email: "demo@example.com" } };
  const profile = {
    id: personaId,
    full_name: person.name,
    role: person.role,
    group_id: person.group_id,
    photo_url: null,
    deleted_at: null,
    // Read back through the demo storage seam. Switching persona hard-reloads
    // the page, so React state alone would show the notice again every time.
    accepted_visibility_notice_at: getNoticeAck(),
  };

  return (
    <StaticAuthProvider session={session} profile={profile}>
      <div style={{ background: C.ink, color: "#fff", position: "sticky", top: 0, zIndex: 60,
        fontFamily: "'Barlow', system-ui, sans-serif" }}>
        <div style={{ maxWidth: 1080, margin: "0 auto", padding: "8px 24px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 12, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            <span style={{ background: C.red, borderRadius: 5, padding: "3px 8px",
              fontSize: 10.5, fontWeight: 800, letterSpacing: ".14em" }}>DEMO</span>
            <span style={{ fontSize: 12.5, opacity: 0.85, lineHeight: 1.35 }}>
              Sample environment - every name and goal is fictional. Changes reset on refresh.
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 11, letterSpacing: ".08em", opacity: 0.7,
              textTransform: "uppercase" }}>Viewing as</span>
            <div style={{ display: "flex", gap: 4, background: "rgba(255,255,255,0.08)",
              padding: 3, borderRadius: 8 }}>
              {DEMO_PERSONAS.map((p) => (
                <button key={p.id}
                  onClick={() => { setPersonaId(p.id); setPersona(p.id); window.location.assign("/demo"); }}
                  style={{ padding: "5px 11px", borderRadius: 6, border: "none", cursor: "pointer",
                    fontSize: 12, fontWeight: 700,
                    background: p.id === personaId ? C.red : "transparent",
                    color: p.id === personaId ? "#fff" : "rgba(255,255,255,0.75)" }}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      <MileMarker key={`${personaId}-${persona?.role}`} initialView={initialView} />
    </StaticAuthProvider>
  );
}
